import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, parse } from 'date-fns';
import { req } from '../utils';

const ContractDialog = ({ open, onClose, accountId }) => {
  const [loading, setLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!open || !accountId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await req('GET', `get_performance?id=${accountId}`);
        setPerformanceData(response);
      } catch (err) {
        console.error('Error fetching performance data:', err);
        setError('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, accountId]);

  const monthlyData = useMemo(() => {
    if (!performanceData?.bins) return [];
    
    // Get the last 12 months of data
    const last12Months = performanceData.bins
      .slice(0, 12)
      .map(bin => ({
        month: format(parse(bin.month, 'yyyy-MM', new Date()), 'MMM yyyy'),
        totalBins: parseInt(bin.total_bins_collected),
        totalCollections: parseInt(bin.total_collections)
      }))
      .reverse(); // Show oldest to newest

    return last12Months;
  }, [performanceData]);

  const handleTabChange = (event, newValue) => {
    event.stopPropagation();
    setActiveTab(newValue);
  };

  const renderContractDetails = () => {
    if (!performanceData?.contract?.[0]) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <Typography color="text.secondary">No contract found</Typography>
        </Box>
      );
    }

    const contract = performanceData.contract[0];
    const endDate = new Date(parseInt(contract.end_time) * 1000); // Convert Unix timestamp to Date
    const isExpired = endDate < new Date();

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {isExpired && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography color="error" sx={{ fontWeight: 'bold' }}>
                    Contract Expired: {format(endDate, 'dd MMM yyyy')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell><strong>Contract Type</strong></TableCell>
              <TableCell>{contract.contract_type?.replace(/_/g, ' ')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Monthly Fee</strong></TableCell>
              <TableCell>${contract.monthly_fee}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Number of Bins</strong></TableCell>
              <TableCell>{contract.nbr_of_bins}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Rate Per {contract.per_item?.replace(/_/g, ' ')}</strong></TableCell>
              <TableCell>${contract.rate_per}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Glass Disposal</strong></TableCell>
              <TableCell>{contract.glass_disposal}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Collections Per Bin</strong></TableCell>
              <TableCell>{contract.collections_per_bin}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Contract Period</strong></TableCell>
              <TableCell>
                <Typography color={isExpired ? 'error' : 'inherit'}>
                  {format(new Date(parseInt(contract.start_time) * 1000), 'dd MMM yyyy')} - {format(endDate, 'dd MMM yyyy')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderPerformanceChart = () => (
    <Box sx={{ width: '100%', height: 400 }}>
      <ResponsiveContainer>
        <BarChart data={monthlyData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month" 
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis />
          <Tooltip />
          <Bar dataKey="totalBins" fill="#00A49B" name="Bins Collected" />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Contract & Performance Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Contract" />
              <Tab label="Performance" />
            </Tabs>
            <Box sx={{ mt: 2 }}>
              {activeTab === 0 ? renderContractDetails() : renderPerformanceChart()}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractDialog;
